import { isPlatformBrowser } from '@angular/common';
import { Injectable, OnDestroy, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { BrowserApiService } from '../browser-api/browser-api.service';
import { StorageService } from '../storage/storage.service';


interface PendingCartProduct {
  id: string;
  bid_price: number;
  quantity: number;
  productDetailOverlay: boolean;
  stayOnCurrentPage?: boolean;
  timestamp: number;
  retryCount?: number;
}

export interface StorePendingProductData {
  id: string;
  bid_price: number;
  quantity: number;
  productDetailOverlay: boolean;
  stayOnCurrentPage: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PendingCartService implements OnDestroy {
  // private platformService = inject(PlatformService);
  private storageService = inject(StorageService);
  private browserApiService = inject(BrowserApiService);

  private readonly STORAGE_KEY = 'pendingCartProduct';
  private readonly EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds
  private readonly MAX_RETRIES = 3; // Maximum number of retries for adding to cart
  // private readonly RETRY_DELAY = 5000; // 5 seconds between retries

  private _pendingProduct = signal<PendingCartProduct | null>(null);
  readonly pendingProduct = this._pendingProduct.asReadonly();

  // Signal for storage events
  private _storageEventSignal = signal<StorageEvent | null>(null);

  // Signal for visibility state changes
  private _visibilityStateSignal = signal<string>('visible');

  // Computed signals for derived state
  readonly hasPendingProduct = computed(() => !!this._pendingProduct());
  readonly pendingProductValid = computed(() => {
    const product = this._pendingProduct();
    return product ? this.isProductValid(product) : false;
  });



  private readonly isBrowser: boolean;

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.isBrowser = isPlatformBrowser(platformId);

    // Initialize from localStorage if available
    if (this.isBrowser) {
      this.restoreFromStorage();
      this.setupBrowserEvents();
    }
  }

  private setupBrowserEvents(): void {
    if (!this.isBrowser) {
      return;
    }

    // Use BrowserApiService for safe window access
    const window = this.browserApiService.getWindow();
    if (!window) {
      return;
    }

    // Listen for storage events from other tabs


    // Ã¢Å“â€¦ MIGRATED: Use effect() to watch for storage events
    effect(() => {
      // Create a signal to trigger storage event handling
      const storageEvent = this._storageEventSignal();
      if (storageEvent) {
        this.restoreFromStorage();
      }
    });

    // Set up native event listeners and update signal
    window.addEventListener('storage', this.handleStorageEvent);

    // Listen for visibility changes to handle tab switches


    // Ã¢Å“â€¦ MIGRATED: Use effect() to watch for visibility changes
    effect(() => {
      const visibilityState = this._visibilityStateSignal();
      if (visibilityState === 'visible') {
        this.restoreFromStorage();
      }
    });

    // Set up native event listener and update signal
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    const handlePageUnload = () => {
      // Only clear pending cart if not on authentication pages
      const currentUrl = window.location.pathname;
      const isAuthPage = currentUrl.includes('/auth/') || currentUrl.includes('/login') || currentUrl.includes('/register');


      if (!isAuthPage) {
        this.clearPendingProduct();
      }
    };

    window.addEventListener('beforeunload', handlePageUnload);
    window.addEventListener('pagehide', handlePageUnload);
  }

  /**
   * Store pending cart product in both signal and localStorage
   */
  storePendingProduct(data: StorePendingProductData): void {
    const product: PendingCartProduct = {
      ...data,
      stayOnCurrentPage: data.stayOnCurrentPage,
      timestamp: Date.now(),
      retryCount: 0
    };

    // Store in signal
    this._pendingProduct.set(product);

    // Backup to localStorage if in browser
    if (this.isBrowser) {
      this.saveToStorage(product);
    }
  }

  /**
   * Get current pending product if valid
   */
  getPendingProduct(): PendingCartProduct | null {
    const product = this._pendingProduct();

    if (product && this.isProductValid(product)) {
      return product;
    }
    // Only clear if there was actually a product that became invalid
    if (product) {
      this.clearPendingProduct();
    } else {
      // Try to restore from storage if signal is empty
      this.restoreFromStorage();
      const restoredProduct = this._pendingProduct();
      if (restoredProduct) {
        return restoredProduct;
      }
    }
    return null;
  }



  /**
   * Increment retry count for a product
   */
  incrementRetry(product: PendingCartProduct): void {
    if ((product.retryCount || 0) < this.MAX_RETRIES) {
      const updatedProduct = {
        ...product,
        retryCount: (product.retryCount || 0) + 1
      };
      this._pendingProduct.set(updatedProduct);
      if (this.isBrowser) {
        this.saveToStorage(updatedProduct);
      }
    } else {
      this.clearPendingProduct();
    }
  }

  /**
   * Clear pending cart product from both signal and localStorage
   */
  clearPendingProduct(): void {
    const pendingProduct = this._pendingProduct();
    const currentTime = Date.now();
    const timePending = pendingProduct ? currentTime - pendingProduct.timestamp : 0;
    // const timePendingSeconds = Math.round(timePending / 1000);

    this._pendingProduct.set(null);
    if (this.isBrowser) {
      try {
        this.storageService.remove(this.STORAGE_KEY);
      } catch (error) {
        // Failed to clear pending cart product from storage
      }
    }
  }

  /**
   * Check if the product is still valid (within expiration time and retry limits)
   */
  isProductValid(product: PendingCartProduct): boolean {
    if (!product) return false;

    const now = Date.now();
    const isWithinExpiration = product.timestamp + this.EXPIRATION_TIME > now;
    const hasRetriesLeft = (product.retryCount || 0) < this.MAX_RETRIES;

    return isWithinExpiration && hasRetriesLeft;
  }

  /**
   * Save to localStorage with error handling
   */
  private saveToStorage(product: PendingCartProduct | null): void {
    if (!this.isBrowser) return;

    try {
      if (product) {
        this.storageService.save(this.STORAGE_KEY, product);
      } else {
        this.storageService.remove(this.STORAGE_KEY);
      }
    } catch (error) {
      // Failed to save to localStorage
    }
  }

  /**
   * Restore from localStorage with validation
   */
  private restoreFromStorage(): void {
    if (!this.isBrowser) return;

    try {
      const product = this.storageService.retrieve(this.STORAGE_KEY) as PendingCartProduct;

      if (product) {
        if (this.isProductValid(product)) {
          this._pendingProduct.set(product);
        } else {
          this.clearPendingProduct();
        }
      }
    } catch (error) {
      this.clearPendingProduct();
    }
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.handleStorageEvent);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  private handleStorageEvent = (event: StorageEvent) => {
    if (event.key === this.STORAGE_KEY) {
      this._storageEventSignal.set(event);
    }
  };

  private handleVisibilityChange = () => {
    this._visibilityStateSignal.set(document.visibilityState);
  };
} 
